// Home's extra query factories of API §3.3 and §3.6 (T-168; ARCH §9.3) and the one optimistic helper the briefing
// panel needs. Web (T-175 / T-177) and the phone (T-181 / T-182) build against these, so a dismissal on one
// device disappears on the other through the same key and the same invalidation rows (`invalidation.ts`).
//
//   const briefing = useQuery(queries.briefing(accountId));
//   const firstRun = useQuery(queries.firstRun(accountId));
//   queryClient.setQueryData(queries.briefing(accountId).queryKey, (d) => d && { ...d, data: optimisticBriefingDismissal(d.data, itemKey) });
//   await client.dismissBriefingItem(accountId, itemKey, 'got_it');
//   await queryClient.invalidateQueries(invalidation.dismissBriefingItem(accountId));
//   // once per session, after the panel has been shown (REQ-023 AC1 — never on load):
//   await client.markVisit(accountId); await queryClient.invalidateQueries(invalidation.markVisit(accountId));
//   // the last card's tap:
//   await client.completeFirstRun(accountId, true); await queryClient.invalidateQueries(invalidation.completeFirstRun(accountId));
//
// **Neither key carries a period**: the briefing is the briefing whatever dates the screen shows (its windows are
// the engine's — 7 days for flags and the best value, the month for pacing, the full days since the visit), and
// first-run is a once-only card set. **Nothing is invented** in the 120 ms before the server answers: the
// dismissal helper removes the item and leaves the section's `empty*` message as the server last said it — the
// server's own answer replaces it on invalidation (D-228: the words are the catalogue's, chosen server-side).

import type { ApiClient } from '../api/ApiClient';
import type { Briefing, FirstRun } from '../types/alerts';
import type { ApiResponse } from '../types/primitives';
import { define, type QueryBase } from './define';
import { queryKeys } from './keys';

/** The §3.3 / §3.6 factories — spread into `createQueries` so a screen reads `queries.briefing(id)` like every other.
 *  `base` is what every factory of one `createQueries` shares (`queryBase`): the stale time and the network mode. */
export function createHomeQueries(client: ApiClient, base: QueryBase) {
  return {
    /** REQ-023's panel: "since your last visit", Needs attention (≤ 3), Going well (≤ 3), the positive empties. */
    briefing: (accountId: string) =>
      define<ApiResponse<Briefing>, ReturnType<typeof queryKeys.briefing>>({
        queryKey: queryKeys.briefing(accountId),
        queryFn: () => client.getBriefing(accountId),
        ...base,
      }),
    /** REQ-005's cards: `show` once (false after complete / skip), ≤ 5 cards, the last card's two forms. */
    firstRun: (accountId: string) =>
      define<ApiResponse<FirstRun>, ReturnType<typeof queryKeys.firstRun>>({
        queryKey: queryKeys.firstRun(accountId),
        queryFn: () => client.getFirstRun(accountId),
        ...base,
      }),
  };
}

export type HomeQueries = ReturnType<typeof createHomeQueries>;

/**
 * `POST …/briefing/items/{itemKey}/dismiss` in flight: the item leaves its section at once ("Got it" and "This is
 * fine" both). The empty message is NOT invented here — it stays whatever the server last sent (`null` while the
 * section had items), and the server's answer fills it on invalidation. Untouched sections are the same objects.
 */
export function optimisticBriefingDismissal<T extends Briefing>(briefing: T, itemKey: string): T;
export function optimisticBriefingDismissal<T extends Briefing>(
  briefing: T | null | undefined,
  itemKey: string,
): T | null;
// T-290 (the T-175 / T-181 ask): a non-null briefing answers a non-null briefing — every caller had written `?? current.data`
export function optimisticBriefingDismissal<T extends Briefing>(
  briefing: T | null | undefined,
  itemKey: string,
): T | null {
  if (briefing === null || briefing === undefined) return null;
  const drop = (items: Briefing['needsAttention']) =>
    items.some((i) => i.itemKey === itemKey) ? items.filter((i) => i.itemKey !== itemKey) : items;
  return {
    ...briefing,
    needsAttention: drop(briefing.needsAttention),
    goingWell: drop(briefing.goingWell),
  };
}
